import React from 'react'

import { myBuddiesLoader, pendingRequestsLoader, acceptRequestLoader, rejectRequestLoader } from './buddiesQueries'

import _ from 'lodash/fp'
import colors from '@fe/ui/colors'
import { Link } from 'react-router-dom'
import { sortByAndMap } from '../../util'

import Typography from '@material-ui/core/Typography'
import List from '@material-ui/core/List'
import Button from '@material-ui/core/Button'

import withStyles from '@material-ui/core/styles/withStyles'
import Spinner from '@fe/ui/Spinner'
import { Edit as EditIcon, ChevronRight as RightIcon, Settings as GearIcon, Check as AcceptIcon, Delete as RejectIcon } from '@material-ui/icons'

import Buddy from './Buddy'
import AddBuddyModal from './AddBuddyModal'
import InviteByEmailModal from './AddBuddyDialog'
import UpsertGhostDialog from './UpsertGhostDialog'
import ManageBuddyDialog from './ManageBuddyDialog'

const styles = _theme => ({
  listHeader: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexDirection: 'row',
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 20
  },
  defaultIcon: {
    fontSize: 32,
    color: colors.secondaryDarkGrey
  },
  acceptIcon: {
    fontSize: 32,
    color: colors.lightGreen
  },
  rejectIcon: {
    fontSize: 32,
    color: colors.lightRed
  }
})

const modals = {
  ADD_BUDDY: 'addBuddyModal',
  INVITE_BY_EMAIL: 'inviteByEmailModal',
  EDIT_BUDDY: 'editBuddyModal'
}

const UPSERT_GHOST_STATUS = {
  none: 'none',
  edit: 'edit',
  create: 'create'
}

const Buddies = ({
  myBuddies: {
    myBuddies,
    getGhosts: ghosts,
    loading,
    refetch: refetchBuddies
  },
  pendingRequests,
  rejectRequest,
  acceptRequest,
  classes
}) => {
  const [activeModal, setActiveModal] = React.useState()

  const pendingBuddies = React.useMemo(() => {
    return _.get('pendingRequests', pendingRequests)
  }, [pendingRequests])

  const handleAccept = React.useCallback((requestId) => () => {
    acceptRequest({ variables: { id: 'foo' } })
      .then(() => {
        pendingRequests.refetch()
      })
      .catch((e) => {
        console.warn('could not accept', requestId, e)
      })
  }, [pendingRequests])

  const handleReject = React.useCallback((requestId) => () => {
    rejectRequest({ variables: { id: 'bar' } })
      .then(() => {
        pendingRequests.refetch()
      })
      .catch((e) => {
        console.warn('could not reject', requestId, e)
      })
  }, [pendingRequests])

  const ghostsWithoutManaged = _.reject({ type: 'managed' }, ghosts)

  const closeModal = () => setActiveModal(undefined)
  const openAddBuddyModal = () => setActiveModal(modals.ADD_BUDDY)
  const openInviteByEmailModal = () => setActiveModal(modals.INVITE_BY_EMAIL)
  const openEditBuddyModal = () => setActiveModal(modals.EDIT_BUDDY)

  const [editProfile, setEditProfile] = React.useState(undefined)

  const [upsertGhostStatus, setUpsertGhostStatus] = React.useState(UPSERT_GHOST_STATUS.none)
  const hideUpsertGhostDialog = React.useCallback(() => {
    setUpsertGhostStatus(UPSERT_GHOST_STATUS.none)
    setEditProfile(undefined)
  }, [setUpsertGhostStatus])

  const showCreateGhostDialog = React.useCallback(() => {
    setUpsertGhostStatus(UPSERT_GHOST_STATUS.create)
    setEditProfile(undefined)
  }, [setUpsertGhostStatus])
  const showEditGhostDialog = React.useCallback(() => { setUpsertGhostStatus(UPSERT_GHOST_STATUS.edit) }, [setUpsertGhostStatus])

  const editGhost = React.useCallback((ghostProfile) => {
    showEditGhostDialog()
    setEditProfile(ghostProfile)
  }, [setEditProfile, showEditGhostDialog])

  const editBuddy = React.useCallback((buddyProfile) => {
    setEditProfile(buddyProfile)
    openEditBuddyModal()
  }, [])

  if (loading) {
    return <Spinner />
  }

  return (
    <div className='outer-container'>
      <div className='container-1280'>

        {!_.isEmpty(pendingBuddies) && (
          <>
            <Typography style={{ fontWeight: 100, fontSize: 24 }}>Buddy Invitations</Typography>
            <List style={{ width: '100%' }}>
              {_.map(request => {
                const buddy = _.get('from', request)
                return (
                  <Buddy
                    key={buddy.id}
                    primary={{
                      action: handleAccept(_.get('id', request)),
                      icon: (
                        <AcceptIcon className={classes.acceptIcon} />
                      )
                    }}
                    secondary={{
                      disabled: false,
                      action: handleReject(_.get('id', request)),
                      icon: <RejectIcon className={classes.rejectIcon} />
                    }}
                    profile={buddy}
                  />
                )
              }, pendingBuddies)}
            </List>

          </>
        )}

        <div className={classes.listHeader}>
          <Typography style={{ fontWeight: 100, fontSize: 24 }}>Your Buddies</Typography>
          <Button onClick={openAddBuddyModal} style={{ color: colors.lightGreen }}>
            Add Buddy
          </Button>
        </div>

        <List style={{ width: '100%' }}>
          {sortByAndMap('name', buddy => {
            return (
              <Buddy
                key={buddy.id}
                primary={{
                  icon: (
                    <Link style={{ lineHeight: 0 }} to={`/u/${_.get('handle', buddy)}`}>
                      <RightIcon className={classes.defaultIcon} />
                    </Link>
                  )
                }}
                secondary={{
                  action: () => editBuddy(buddy),
                  icon: <GearIcon className={classes.defaultIcon} />
                }}
                profile={buddy}
              />
            )
          }, myBuddies)}
        </List>

        <div className={classes.listHeader}>
          <Typography style={{ fontWeight: 100, fontSize: 24 }}>Your Local Buddies</Typography>
          <Button onClick={showCreateGhostDialog} style={{ color: colors.lightGreen }}>
            Add Local Buddy
          </Button>
        </div>
        <List style={{ width: '100%' }}>
          {sortByAndMap('name', (buddy) => {
            return (
              <Buddy
                primary={{
                  icon: (
                    <Link style={{ lineHeight: 0 }} to={`/u/${_.get('handle', buddy)}`}>
                      <RightIcon className={classes.defaultIcon} />
                    </Link>
                  )
                }}
                secondary={{
                  action: () => editGhost(buddy),
                  icon: <EditIcon className={classes.defaultIcon} />
                }}
                profile={buddy}
              />
            )
          }, ghostsWithoutManaged)}
        </List>

      </div>

      <ManageBuddyDialog
        open={activeModal === modals.EDIT_BUDDY}
        hide={closeModal}
        buddy={editProfile}
        refetchBuddies={refetchBuddies}
      />

      <AddBuddyModal
        open={activeModal === modals.ADD_BUDDY}
        handleClose={closeModal}
        handleInviteByEmail={openInviteByEmailModal}
        handleAddLocalBuddy={() => {
          closeModal()
          showCreateGhostDialog()
        }}
      />

      <UpsertGhostDialog
        mode='create'
        open={upsertGhostStatus === UPSERT_GHOST_STATUS.create}
        hide={hideUpsertGhostDialog}
      />

      <UpsertGhostDialog
        mode='edit'
        open={upsertGhostStatus === UPSERT_GHOST_STATUS.edit && !_.isEmpty(editProfile)}
        hide={hideUpsertGhostDialog}
        ghostProfile={editProfile}
      />

      <InviteByEmailModal
        open={activeModal === modals.INVITE_BY_EMAIL}
        hide={closeModal}
      />
    </div>
  )
}

export { default as Suggest } from './Suggest'

export default _.compose(
  withStyles(styles),
  myBuddiesLoader,
  pendingRequestsLoader,
  acceptRequestLoader,
  rejectRequestLoader
)(Buddies)
